import {Component, Inject, Input, Optional, ViewChild} from '@angular/core';
import {NbDialogService} from '@nebular/theme';
import {UsersService} from "../shared/users.service";
import {SubscribedComponent} from "../subscribed.component";
import {AuthService} from "../shared/auth.service";
import {User, UserStatus} from "../interfaces/user";
import {BehaviorSubject} from "rxjs";
import {MetricsService, USE_METRICS_API, DEFAULT_HORIZON_WEEKS} from "../shared/metrics.service";
import {NotifierService} from "../shared/notifier.service";
import {MetricScope, DashboardConfig, CompanyWidgetKey, DashboardLayout, WidgetLayout, MetricResult} from "../interfaces/metrics";
import {MemberDrillPanelComponent} from './member-drill/member-drill-panel.component';
import {DrilldownPanelComponent} from './drilldown/drilldown-panel.component';

// Canonical company widget order + default 12-col slots. Mirror of the backend
// MetricsService::COMPANY_DEFAULT_LAYOUT, consulted only for the pre-GET first paint
// and in mock/dev mode (in prod getDashboardLayout returns the server-normalized full
// map). Keyed by CompanyWidgetKey so a new/mistyped company widget id fails to compile
// here rather than silently rendering at a wrong default.
//
// Phase 0 cut (spec §8): company.bench_forecast's chart moved INTO the company.bench
// tile (count + share + roll-off duration + the forecast trend, one block instead of
// two), so it no longer gets its own grid item and company.bench grew taller to fit.
// bench_roster and coming_free were demoted below attention/util_timeseries: they read
// as reference lists, not the operational/financial risk signals the tab leads with.
// The company.bench_forecast entry is kept ONLY because CompanyWidgetKey (derived from
// every company.* MetricKey, since the merged chart still queries it) forces this
// Record to have an entry for it; its value is dead (COMPANY_WIDGET_ORDER excludes it).
const COMPANY_DEFAULT_LAYOUT: Record<CompanyWidgetKey, WidgetLayout> = {
  'company.summary':          { x: 0, y: 0,  w: 12, h: 2 },
  'company.margin':           { x: 0, y: 2,  w: 6,  h: 2 },
  'company.bench':            { x: 6, y: 2,  w: 6,  h: 5 },
  'company.margin_by_client': { x: 0, y: 7,  w: 12, h: 4 },
  'company.attention':        { x: 0, y: 11, w: 12, h: 3 },
  'company.util_timeseries':  { x: 0, y: 14, w: 12, h: 3 },
  'company.bench_roster':     { x: 0, y: 17, w: 12, h: 4 },
  'company.coming_free':      { x: 0, y: 21, w: 12, h: 3 },
  // Dead entry, parked on its own row: a fully overlapping pair is an invalid layout even
  // when nothing renders it. Byte-identical to the backend constant.
  'company.bench_forecast':   { x: 0, y: 24, w: 12, h: 3 },
};

// Company widget ids in canonical order (the grid's rendered set is this minus hidden).
// Excludes company.bench_forecast: its chart is nested inside the company.bench tile now.
const COMPANY_WIDGET_ORDER = (Object.keys(COMPANY_DEFAULT_LAYOUT) as CompanyWidgetKey[])
  .filter(id => id !== 'company.bench_forecast');

// Min column span per widget so text/tables never crush when resized. company.bench
// bumped 2->3 (was bench_forecast's own floor) now that its chart lives there too.
// company.bench_forecast's own entry is dead (never rendered as its own grid item,
// so minW() is never called with it), kept only because CompanyWidgetKey forces
// this Record to have every company.* key, same as COMPANY_DEFAULT_LAYOUT above.
const COMPANY_MIN_W: Record<CompanyWidgetKey, number> = {
  'company.summary': 4, 'company.margin': 2, 'company.bench': 3,
  'company.margin_by_client': 4, 'company.bench_roster': 4, 'company.attention': 4,
  'company.util_timeseries': 4, 'company.coming_free': 3, 'company.bench_forecast': 3,
};

@Component({
    selector: 'bst-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    standalone: false
})
export class DashboardComponent extends SubscribedComponent{

  user: User;

  tab: 'self' | 'team' | 'company' = 'self';
  managedDepartments: { id: number; name: string }[] = [];
  selectedDeptId: number | null = null;
  canCompany = false;
  canConfigure = false;                   // admin gate for the Edit button
  companyTarget: number | null = null;    // raw per-org target override (seeds the edit input)
  companyMarginTarget: number | null = null;  // raw per-org margin goal override (seeds the edit input)
  config: DashboardConfig = { hidden: [], horizonWeeks: DEFAULT_HORIZON_WEEKS };  // applied per-tenant config
  // The actor's own view_bill_rate grant for this tenant; threaded into the member-drill
  // so contribution.self renders there only when the viewing manager holds the grant.
  actorCanSeeBillRate = false;

  editing = false;
  draft: DashboardConfig = { hidden: [], horizonWeeks: DEFAULT_HORIZON_WEEKS };   // working copy while editing
  draftTarget: number | null = null;
  draftMarginTarget: number | null = null;
  savingConfig = false;

  // Edit has two sub-modes, never shown together: Show/hide (checkboxes + params) and
  // Arrange (drag handles). Splits the previously-simultaneous controls without touching
  // the underlying draft state, which stays shared across both (#1075).
  configMode: 'visibility' | 'arrange' = 'visibility';
  setConfigMode(mode: 'visibility' | 'arrange'): void { this.configMode = mode; }

  // One unified Edit mode: show/hide + params (persisted per-tenant) AND drag/resize
  // (persisted per-user). companyLayout is the applied layout; layoutDraft is the
  // working copy the grid mutates while editing.
  companyLayout: DashboardLayout = {};   // applied per-user layout (server-normalized)
  layoutDraft: DashboardLayout = {};     // working copy while editing (grid mutates it)
  layoutResetTick = 0;                   // bumped by Reset to force the grid to reload
  // False until the initial layout GET succeeds (covers both still-loading and failed).
  // Until then companyLayout is {}, so dragging is disabled in Edit and the layout PUT
  // is skipped on Save, so we can never overwrite the user's stored layout with defaults.
  layoutLoaded = false;
  // Content revisions handed out by the last read of each scope, echoed back on
  // save (#1107). Undefined until the read lands, and on a response from before
  // this shipped, in which case the save is unguarded exactly as it used to be.
  configRevision?: string;
  layoutRevision?: string;
  // True once the initial layout GET has SETTLED (success OR failure), mirroring
  // SelfDashboardGridComponent.selfLayoutFetched. The grid is not created until this is
  // true, so gridstack initializes ONCE over the loaded layout instead of over the
  // catalog default; without this gate, a narrow viewport could init the grid with
  // defaults while the real layout was still in flight, then defer picking it up until
  // the viewport is back at 12 columns (reinit() never rebuilds over collapsed DOM). On
  // a failed load we still render (with defaults) so the tab is never blank.
  layoutFetched = false;

  // The grid is draggable only in Edit mode, only once a real layout has loaded, and only
  // in the Arrange sub-mode (Show/hide and Arrange are never both active — #1075).
  get layoutEditable(): boolean { return this.editing && this.layoutLoaded && this.configMode === 'arrange'; }

  // Stable signature of the rendered company widget set (order minus hidden), for the
  // grid's idsKey resync trigger. A getter returning an equal string on an unchanged
  // set is Object.is-equal, so it does not thrash the grid.
  get visibleCompanyKey(): string {
    return COMPANY_WIDGET_ORDER.filter(id => this.showHost(id)).join(',');
  }

  // Target line feeds the summary bullet (#cs) + trend line (#ct) from the widget
  // payload, not the query, so a saved target change needs an explicit refetch of
  // these two hosts. Optional: undefined when the widget is hidden in the applied
  // config (host not rendered outside edit mode); the ?. guard covers that.
  @ViewChild('cs') private summaryHost?: { retry: () => void };
  @ViewChild('ct') private trendHost?: { retry: () => void };

  get canTeam(): boolean { return this.managedDepartments.length > 0; }
  get deptScope(): MetricScope { return { type: 'department', id: this.selectedDeptId ?? 0 }; }
  get companyScope(): MetricScope { return { type: 'company' }; }

  constructor(private userService: UsersService,
              private auth: AuthService,
              private metrics: MetricsService,
              private notifier: NotifierService,
              private dialog: NbDialogService,
              @Optional() @Inject(USE_METRICS_API) private useApi: boolean) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.bootstrap();
  }

  get isNew(){
    return this.user && this.user.user_to_client && !this.user.user_to_client.onboarded_at && this.user.user_to_client.status;
  }

  // true only when metrics fall back to MetricsService's deterministic mock
  // (USE_METRICS_API=false). Live since the flip: the real /api/metrics endpoint
  // serves every widget, so the example-data banner stays hidden.
  get isExampleData(): boolean {
    return !this.useApi;
  }

  private async bootstrap(){
    this.loading.next(true);
    const cur: User = this.auth.user.getValue();
    if(cur) {
      this.user = await this.userService.getUser(cur.id).catch(() => null);
    }
    this.loading.next(false);

    // A new user gets the `@if (isNew)` branch only: bst-new-user, no tiles, no
    // grid. The three loads below all hit /api/metrics/*, which the dashboard
    // flag gates -- on a dark tenant (where the guard still routes an isNew user
    // here) they 404, and the layout one raises a danger toast over the
    // onboarding form. Nothing they populate is rendered on this branch anyway.
    if(this.isNew){
      return;
    }

    this.subs.push(this.metrics.managedDepartments().subscribe({
      next: depts => {
        this.managedDepartments = depts;
        this.selectedDeptId = depts.length ? depts[0].id : null;
      },
      error: err => console.error('dashboard: managed departments load failed', err),
    }));
    this.subs.push(this.metrics.companyAccess().subscribe({
      next: a => {
        this.canCompany = a.canView;
        this.canConfigure = a.canConfigure;
        this.companyTarget = a.target;
        this.companyMarginTarget = a.marginTarget;
        this.actorCanSeeBillRate = a.viewBillRate;
        // Defensive default: never let a missing config null the whole tab (showHost
        // would throw on undefined.includes).
        this.config = a.config ?? { hidden: [], horizonWeeks: DEFAULT_HORIZON_WEEKS };
        this.configRevision = a.configRevision;
      },
      error: err => console.error('dashboard: company access load failed', err),
    }));
    this.subs.push(this.metrics.getDashboardLayout('company').subscribe({
      next: ({ layout, revision }) => {
        this.companyLayout = layout;
        this.layoutRevision = revision;
        this.layoutLoaded = true;
        this.layoutFetched = true;   // renders the grid, now over the loaded layout
        // If the GET resolved after Edit was already entered, re-seed the draft so a
        // Save writes the real layout, not the defaults shown during the load window.
        if (this.editing) { this.layoutDraft = this.seedLayoutDraft(layout); }
      },
      error: err => {
        // Leave layoutLoaded false: dragging stays disabled and Save skips the layout
        // PUT, so a failed load cannot overwrite a stored layout with defaults. Still
        // render the grid (layoutFetched) so the tab is never blank, falling back to
        // client defaults.
        this.layoutFetched = true;
        console.error('dashboard: company layout load failed', err);
        this.notifier.notify('dashboard.company.layout.load_error', null, 'danger');
      },
    }));
  }

  // Grid position for a widget: the draft while editing, otherwise the applied layout,
  // falling back to the catalog default (covers the mock's {} and any omitted widget).
  cell(id: CompanyWidgetKey): WidgetLayout {
    return (this.editing ? this.layoutDraft : this.companyLayout)[id]
      ?? COMPANY_DEFAULT_LAYOUT[id]
      ?? { x: 0, y: 0, w: 12, h: 3 };
  }
  minW(id: CompanyWidgetKey): number { return COMPANY_MIN_W[id] ?? 1; }

  // Reset positions to the catalog default in the draft only (persisted on Save). Bump
  // the tick so the grid reloads even though it is mid-edit (its normal resync is gated
  // off during editing to protect an active drag).
  resetLayout(): void {
    this.layoutDraft = this.seedLayoutDraft(COMPANY_DEFAULT_LAYOUT);
    this.layoutResetTick++;
  }

  // Filter a layout source (companyLayout, a GET response, or the default map) down to
  // the RENDERED widget set (COMPANY_WIDGET_ORDER), dropping company.bench_forecast's
  // dead entry: it never renders as its own grid item (its chart lives inside
  // company.bench now), so nothing ever overrides it and effectiveLayout() always
  // refills it from its default on the next GET. Carrying that phantom cell into a
  // draft means a Save that never touches the grid PUTs it unchanged; if a real
  // widget was ever dragged onto its row, the PUT then 422s on a same-row overlap the
  // user can neither see nor fix. Used by every draft-seeding path (editCompany, the
  // late-GET reseed, and Reset) so there is exactly one place to get this filter right.
  private seedLayoutDraft(source: DashboardLayout): DashboardLayout {
    const draft: DashboardLayout = {};
    for (const id of COMPANY_WIDGET_ORDER) {
      if (source[id]) { draft[id] = source[id]; }
    }
    return draft;
  }

  // A widget renders in display mode only when not hidden; in edit mode all hosts
  // stay mounted (dimmed if draft-hidden) so toggling never remounts a widget and
  // loses an in-progress param input.
  showHost(id: CompanyWidgetKey): boolean { return this.editing || !this.config.hidden.includes(id); }
  isDraftHidden(id: CompanyWidgetKey): boolean { return this.draft.hidden.includes(id); }
  toggleHidden(id: CompanyWidgetKey): void {
    const i = this.draft.hidden.indexOf(id);
    if (i >= 0) { this.draft.hidden.splice(i, 1); } else { this.draft.hidden.push(id); }
  }

  editCompany(): void {
    // Snapshot both drafts; Cancel discards them and the applied config + layout remain.
    this.draft = { hidden: [...this.config.hidden], horizonWeeks: this.config.horizonWeeks };
    this.draftTarget = this.companyTarget;
    this.draftMarginTarget = this.companyMarginTarget;
    // Seed from the RENDERED widget set only; see seedLayoutDraft for why.
    this.layoutDraft = this.seedLayoutDraft(this.companyLayout);
    this.configMode = 'visibility';
    this.editing = true;
  }

  cancelEdit(): void { this.editing = false; }

  // True when the tenant config draft differs from what is applied. Compared at
  // save time rather than tracked per keystroke: the grid mutates layoutDraft in
  // place, so an event-based dirty flag would have to be threaded through every
  // mutation path and would go stale the first time one was missed (#1107).
  private configDirty(): boolean {
    return this.draftTarget !== this.companyTarget
      || this.draftMarginTarget !== this.companyMarginTarget
      || this.draft.horizonWeeks !== this.config.horizonWeeks
      || [...this.draft.hidden].sort().join('|') !== [...this.config.hidden].sort().join('|');
  }

  private layoutDirty(): boolean {
    // Against the draft the applied layout WOULD seed, not the applied layout
    // itself: seedLayoutDraft narrows to the rendered widget set, so comparing
    // the two directly reports a difference on every save.
    const applied = this.seedLayoutDraft(this.companyLayout);
    const canonical = (l: DashboardLayout) => JSON.stringify(
      Object.keys(l).sort().map(id => [id, l[id].x, l[id].y, l[id].w]),
    );
    return canonical(this.layoutDraft) !== canonical(applied);
  }

  saveConfig(): void {
    if (this.savingConfig) { return; }
    // Send only the scope that actually changed. An admin who moved a widget no
    // longer PATCHes the tenant config at all, which is what silently reverted a
    // colleague's targets with a stale draft (#1107).
    const needConfig = this.configDirty();
    const needLayout = this.layoutLoaded && this.layoutDirty();
    if (!needConfig && !needLayout) { this.editing = false; return; }

    this.savingConfig = true;
    // Save the two scopes sequentially so the UI is never inconsistent with the server:
    // config (per-tenant) is applied on its own success, then positions (per-user) are
    // saved and applied. The layout PUT is skipped until a layout has actually loaded,
    // so it can never overwrite the stored layout with defaults.
    if (!needConfig) { this.saveLayoutThenFinish(); return; }

    const body = {
      hidden: [...this.draft.hidden], target: this.draftTarget,
      marginTarget: this.draftMarginTarget, horizonWeeks: this.draft.horizonWeeks,
      revision: this.configRevision,
    };
    this.subs.push(this.metrics.updateDashboardConfig(body).subscribe({
      next: res => {
        this.config = res.config;
        this.companyTarget = res.target;
        this.companyMarginTarget = res.marginTarget;
        this.configRevision = res.revision;
        // Visibility + horizon (a query input) reapply automatically; the target line
        // is payload-derived, so refetch the two hosts that draw it.
        this.summaryHost?.retry();
        this.trendHost?.retry();
        if (!needLayout) { this.finishSave(); return; }
        this.saveLayoutThenFinish();
      },
      error: err => {
        // 409: someone else saved while this draft was open. Take their values so the
        // editor is looking at reality, tell the user their change did not land, and
        // stay in edit mode so they can redo it on top.
        if (err?.status === 409) {
          this.config = err.error?.config ?? this.config;
          this.companyTarget = err.error?.target ?? null;
          this.companyMarginTarget = err.error?.marginTarget ?? null;
          this.configRevision = err.error?.revision;
          this.draft = { hidden: [...this.config.hidden], horizonWeeks: this.config.horizonWeeks };
          this.draftTarget = this.companyTarget;
          this.draftMarginTarget = this.companyMarginTarget;
          this.summaryHost?.retry();
          this.trendHost?.retry();
          this.notifier.notify('dashboard.company.config.conflict', null, 'warning');
          this.savingConfig = false;
          return;
        }
        // Config write failed: keep edit mode open (drafts intact) and tell them, so
        // nothing looks saved that was not.
        console.error('dashboard: save config failed', err?.error?.message ?? err);
        this.notifier.notify('dashboard.company.config.save_error', null, 'danger');
        this.savingConfig = false;
      },
    }));
  }

  private saveLayoutThenFinish(): void {
    this.subs.push(this.metrics.saveDashboardLayout('company', this.layoutDraft, this.layoutRevision).subscribe({
      next: ({ layout, revision }) => {
        this.companyLayout = layout;
        this.layoutRevision = revision;
        this.finishSave();
      },
      error: err => {
        if (err?.status === 409) {
          // Another tab arranged this dashboard after this one loaded. Show theirs
          // rather than flattening it, and leave the editor open.
          this.companyLayout = err.error?.layout ?? this.companyLayout;
          this.layoutRevision = err.error?.revision;
          this.layoutDraft = this.seedLayoutDraft(this.companyLayout);
          this.notifier.notify('dashboard.company.layout.conflict', null, 'warning');
          this.savingConfig = false;
          return;
        }
        // Any config write already saved + applied; only positions failed. Keep edit
        // mode open so the user can retry without the config looking unsaved.
        console.error('dashboard: save layout failed', err?.error?.message ?? err);
        this.notifier.notify('dashboard.company.config.save_error', null, 'danger');
        this.savingConfig = false;
      },
    }));
  }

  private finishSave(): void {
    this.editing = false;
    this.savingConfig = false;
  }

  // Draft-only edits (not persisted until Save). Text inputs so an invalid keystroke
  // snaps back to the last good value instead of masquerading as a deliberate clear.
  onDraftTarget(raw: string, input: HTMLInputElement): void {
    const t = (raw ?? '').trim();
    if (t !== '' && Number.isNaN(Number(t))) { input.value = this.draftTarget === null ? '' : String(this.draftTarget); return; }
    this.draftTarget = t === '' ? null : Math.max(0, Math.min(100, Math.round(Number(t))));
    input.value = this.draftTarget === null ? '' : String(this.draftTarget);
  }

  onDraftMarginTarget(raw: string, input: HTMLInputElement): void {
    const t = (raw ?? '').trim();
    if (t !== '' && Number.isNaN(Number(t))) { input.value = this.draftMarginTarget === null ? '' : String(this.draftMarginTarget); return; }
    this.draftMarginTarget = t === '' ? null : Math.max(0, Math.min(100, Math.round(Number(t))));
    input.value = this.draftMarginTarget === null ? '' : String(this.draftMarginTarget);
  }

  onDraftHorizon(raw: string, input: HTMLInputElement): void {
    const t = (raw ?? '').trim();
    const n = Number(t);
    // Empty is not a clear (horizon has no auto): treat blank like invalid and snap
    // back, so Number('')===0 never silently persists the minimum (4).
    if (t === '' || !Number.isFinite(n)) { input.value = String(this.draft.horizonWeeks); return; }
    this.draft.horizonWeeks = Math.max(4, Math.min(13, Math.round(n)));
    input.value = String(this.draft.horizonWeeks);
  }

  onTabChange(t: { tabTitle?: string; tabId?: string }): void {
    this.tab = t.tabId === 'team' ? 'team' : t.tabId === 'company' ? 'company' : 'self';
  }

  // Same shape as the team dashboard's openDrill. The panel never fetches: the
  // drawer only re-renders the payload the tile already fetched under the same
  // grant (view_margin for the margin metrics is enforced per request in
  // MetricsController), so no extra financial-visibility check is needed here.
  // Note the drawer may show payload fields the tile does not render — that is
  // authorized data, not a leak.
  openDrill(result: MetricResult | undefined): void {
    if (!result) { return; }
    this.dialog.open(DrilldownPanelComponent, { context: { result }, dialogClass: 'drawer-right' });
  }

  openMember(member: { userId: number; name: string }): void {
    this.dialog.open(MemberDrillPanelComponent, {
      context: { userId: member.userId, name: member.name, anchor: this.anchor, actorCanSeeBillRate: this.actorCanSeeBillRate },
      dialogClass: 'drawer-right',
    });
  }

  // Period the dashboard is viewing. Defaults to today; the prev/next selector
  // moves it a week at a time so a user can reach a period that actually has
  // data (staging/real dashboards are empty at the start of the current week).
  //
  // "Today" is the Amsterdam calendar date (the backend's period timezone), NOT
  // the UTC date: at 00:00–02:00 Amsterdam, new Date().toISOString() is still
  // yesterday, which on a week/month boundary would resolve the wrong period.
  readonly today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Amsterdam' }).format(new Date());
  anchor = this.today;
  readonly selfScope = { type: 'self' } as const;
  // The personal view is the only dashboard tier with a month-capable metric. Keep
  // its selected period in the parent so the global period navigator always names
  // and advances the same window as the cards it controls.
  selfPeriod: 'week' | 'month' = 'week';

  get isViewingMonth(): boolean { return this.tab === 'self' && this.selfPeriod === 'month'; }

  onSelfPeriodChange(period: 'week' | 'month'): void {
    if (this.selfPeriod === period) { return; }
    this.selfPeriod = period;
    this.anchor = period === 'month' ? this.startOfMonth(this.anchor) : this.startOfWeek(this.anchor);
  }

  prevPeriod(): void {
    this.anchor = this.isViewingMonth
      ? this.shiftMonths(this.anchor, -1)
      : this.shiftDays(this.anchor, -7);
  }

  nextPeriod(): void {
    if (this.atCurrentPeriod) { return; }          // never navigate into the future
    const next = this.isViewingMonth
      ? this.shiftMonths(this.anchor, 1)
      : this.shiftDays(this.anchor, 7);
    this.anchor = next > this.today ? this.today : next;
  }

  get atCurrentWeek(): boolean {
    return this.startOfWeek(this.anchor) === this.startOfWeek(this.today);
  }

  get atCurrentPeriod(): boolean {
    return this.isViewingMonth
      ? this.startOfMonth(this.anchor) === this.startOfMonth(this.today)
      : this.atCurrentWeek;
  }

  // "22–28 Jun 2026" — the anchor week's Mon–Sun date range, so the user can see
  // which dates (and month) the widgets are reporting.
  get periodRange(): string {
    if (this.isViewingMonth) {
      return new Date(this.startOfMonth(this.anchor) + 'T12:00:00Z')
        .toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
    }
    const mon = this.startOfWeek(this.anchor);
    const sun = this.shiftDays(mon, 6);
    // Format via timeZone: 'UTC' so the day shown matches the ISO date exactly,
    // independent of the browser's own timezone.
    const fmt = (iso: string, opts: Intl.DateTimeFormatOptions) =>
      new Date(iso + 'T12:00:00Z').toLocaleDateString(undefined, { ...opts, timeZone: 'UTC' });
    // Show the start's month (and year) only when it differs from the end's, so a
    // week crossing a month/year boundary reads "29 Jun – 5 Jul 2026", not "29–5 Jul 2026".
    const sameMonth = mon.slice(0, 7) === sun.slice(0, 7);
    const sameYear = mon.slice(0, 4) === sun.slice(0, 4);
    const start = sameMonth
      ? fmt(mon, { day: 'numeric' })
      : fmt(mon, sameYear ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' });
    const end = fmt(sun, { day: 'numeric', month: 'short', year: 'numeric' });
    return `${start} – ${end}`;
  }

  // Date math on a plain YYYY-MM-DD anchor, done at UTC noon with UTC getters so
  // it never crosses a local-midnight/DST boundary and shifts the day.
  private shiftDays(iso: string, days: number): string {
    const d = new Date(iso + 'T12:00:00Z');
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
  }

  private shiftMonths(iso: string, months: number): string {
    const d = new Date(this.startOfMonth(iso) + 'T12:00:00Z');
    d.setUTCMonth(d.getUTCMonth() + months);
    return d.toISOString().slice(0, 10);
  }

  private startOfMonth(iso: string): string {
    return `${iso.slice(0, 7)}-01`;
  }

  private startOfWeek(iso: string): string {
    const d = new Date(iso + 'T12:00:00Z');
    d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));   // back up to Monday (ISO)
    return d.toISOString().slice(0, 10);
  }

  // ISO week number, computed at UTC noon with UTC getters so it agrees with
  // startOfWeek/shiftDays and never drifts a day on browsers west of UTC.
  weekNo(iso: string): number {
    const d = new Date(iso + 'T12:00:00Z');
    d.setUTCDate(d.getUTCDate() + 3 - ((d.getUTCDay() + 6) % 7));
    const week1 = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
    return 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getUTCDay() + 6) % 7)) / 7);
  }

  // Same bands as bst-suggested-vacancies' getScoreColor / bst-company-coming-free's
  // matchScoreBand, so a bench-to-market fit score reads the same color everywhere.
  matchScoreBand(score: number | null): string {
    if (score === null) return 'basic';
    if (score >= 80) return 'high';
    if (score >= 50) return 'med';
    if (score > 0) return 'low';
    return 'basic';
  }

  get headerTitle(): string | { title: string; translate: boolean | Object } {
    return 'Dashboard';
  }
}
